import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AccountLayout } from "./AccountLayout";
import { Button } from "../ui/button";
import api, { formatEUR, formatCrypto, ASSET_ICONS } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { AssetGlyph } from "../Brand";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import BuyModal from "../BuyModal";
import { toast } from "sonner";
import { TrendingUp, Receipt, Star, ShieldAlert, ArrowRight, Repeat } from "lucide-react";

const STATUS_TONE = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  awaiting_payment: "bg-amber-50 text-amber-700 border-amber-200",
  sourcing_liquidity: "bg-blue-50 text-blue-700 border-blue-200",
  sending_crypto: "bg-blue-50 text-blue-700 border-blue-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AccountDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyAsset, setBuyAsset] = useState(null);
  const [buyAmount, setBuyAmount] = useState(null);

  useEffect(() => {
    api.get("/v1/orders")
      .then((r) => setOrders(r.data.orders || []))
      .finally(() => setLoading(false));
  }, []);

  const completed = orders.filter((o) => o.status === "completed");
  const totalSpent = completed.reduce((sum, o) => sum + (o.fiat_amount || 0), 0);

  // Favourite asset = most ordered
  const counts = {};
  orders.forEach((o) => { counts[o.asset] = (counts[o.asset] || 0) + 1; });
  const favourite = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";

  const repeatLast = async () => {
    const last = orders[0];
    if (!last) return;
    setBuyAsset(last.asset);
    setBuyAmount(last.fiat_amount);
    setBuyOpen(true);
  };

  const kycNeeded = !user?.kyc_tier || user.kyc_tier < 1;

  return (
    <AccountLayout
      title={`Welcome back, ${user?.name?.split(" ")[0] || ""}`}
      subtitle="Your orders, wallets and verification at a glance."
      onOpenBuy={() => { setBuyAsset(null); setBuyAmount(null); setBuyOpen(true); }}
    >
      {kycNeeded && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="kyc-banner">
          <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-bold text-amber-900">Complete identity verification</div>
            <div className="text-sm text-amber-800 mt-0.5">Unlock €2,000+ monthly limits in under 5 minutes.</div>
          </div>
          <Button onClick={() => navigate("/account/kyc")} size="sm"
                  className="bg-amber-700 hover:bg-amber-800 text-white rounded-lg shrink-0"
                  data-testid="kyc-banner-cta">
            Start <ArrowRight className="w-3.5 h-3.5 ml-0.5" />
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="dashboard-stats">
        <StatCard label="Total spent" value={formatEUR(totalSpent)} icon={<TrendingUp className="w-3.5 h-3.5 text-primary" />} testid="stat-spent" />
        <StatCard label="Total orders" value={orders.length} icon={<Receipt className="w-3.5 h-3.5 text-primary" />} testid="stat-orders" />
        <StatCard label="Favourite asset" value={favourite} icon={<Star className="w-3.5 h-3.5 text-primary" />} testid="stat-favourite" />
      </div>

      {orders.length > 0 && (
        <div className="flex items-center gap-3 mb-6">
          <Button onClick={repeatLast} variant="outline" className="rounded-xl gap-1.5" data-testid="repeat-last-btn">
            <Repeat className="w-3.5 h-3.5" /> Buy last order again ({orders[0]?.asset} · {formatEUR(orders[0]?.fiat_amount)})
          </Button>
        </div>
      )}

      <div className="xch-card overflow-hidden" data-testid="recent-orders-card">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="font-display text-base font-bold text-foreground">Recent orders</div>
          <button onClick={() => navigate("/account/orders")}
                  className="text-xs font-semibold text-primary hover:underline"
                  data-testid="see-all-orders-btn">View all →</button>
        </div>
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1 space-y-1.5"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-44" /></div>
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="p-10 text-center space-y-3" data-testid="empty-state">
            <div className="text-sm text-muted-foreground">No orders yet.</div>
            <Button onClick={() => { setBuyAsset(null); setBuyOpen(true); }}
                    className="bg-primary hover:bg-primary/90 text-white rounded-xl"
                    data-testid="empty-buy-btn">
              Make your first purchase
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border" data-testid="orders-list">
            {orders.slice(0, 5).map((o) => (
              <button key={o.id} onClick={() => navigate(`/widget/status/${o.id}`)}
                      className="w-full flex items-center gap-3 p-4 hover:bg-muted/30 transition-colors text-left"
                      data-testid={`order-row-${o.id}`}>
                <AssetGlyph symbol={o.asset} color={ASSET_ICONS[o.asset]} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-foreground truncate">
                    {formatCrypto(o.receive_amount)} {o.asset}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatEUR(o.fiat_amount)} · {new Date(o.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
                <Badge variant="outline" className={`${STATUS_TONE[o.status] || "bg-muted text-muted-foreground"} rounded-full font-semibold text-[10px] uppercase tracking-wider border`}>
                  {o.status.replace(/_/g, " ")}
                </Badge>
              </button>
            ))}
          </div>
        )}
      </div>

      <BuyModal open={buyOpen} onOpenChange={setBuyOpen} initialAsset={buyAsset} initialAmount={buyAmount} />
    </AccountLayout>
  );
}

const StatCard = ({ label, value, icon, testid }) => (
  <div className="xch-card p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
      {icon} {label}
    </div>
    <div className="font-display text-2xl font-extrabold text-foreground mt-1 tabular-nums">{value}</div>
  </div>
);
